import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { CustomDateRangePicker } from '@components/CustomDateRangePicker'
import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { prepareCustomListFilterEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoCustomListFilterEventType } from '@modules/pendo/types'
import {
  SHORTLIST_FILTERS,
  SHORTLIST_FILTER_OPERATORS_BY_CUSTOM_FIELD,
  SHORTLIST_FILTER_OPERATORS_BY_FIELD,
  SHORTLIST_SEARCH_DEBOUNCE_TIME,
  SHORTLIST_VALUE_TYPE_BY_OPERATOR,
} from '@modules/shortlist/constants'
import {
  CustomColumnType,
  FilterCombinator,
  FilterOperator,
  Shortlist,
  ShortlistColumnFilter,
  ShortlistFilterField,
  ShortlistFilterFieldType,
} from '@modules/shortlist/types'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { colors } from '@theme/variables'

interface ShortlistFilterPopoverProps {
  anchor: CustomPopoverProps['anchor']
  activeShortlist?: Shortlist
  existingFilters?: ShortlistColumnFilter[]
  handleClose: (value: Nullable<HTMLElement>) => void
  filtersUpdated: (filters: ShortlistColumnFilter[]) => void
}

const DEFAULT_FILTER = {
  attribute: SHORTLIST_FILTERS[0].attribute,
  type: SHORTLIST_FILTERS[0].attribute,
  operator: FilterOperator.CONTAINS,
  combinator: '',
}

const ShortlistFilterPopover = ({
  anchor,
  activeShortlist,
  existingFilters,
  handleClose,
  filtersUpdated,
}: ShortlistFilterPopoverProps) => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ShortlistColumnFilter[]>(existingFilters || [DEFAULT_FILTER])
  const debouncedFilters = useDebouncedValue(filters, SHORTLIST_SEARCH_DEBOUNCE_TIME)
  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { isDesktop, isMobile } = useScreenDimensions()
  const filterColumns = useMemo(
    () =>
      SHORTLIST_FILTERS.reduce((acc, field) => {
        if (field.attribute !== ShortlistFilterField.customFieldValues) {
          acc.push({
            ...field,
            type: field.attribute,
          })
        } else {
          acc.push(
            ...(activeShortlist?.customFields?.map(customField => ({
              attribute: customField.id!,
              label: customField.fieldName!,
              type: ShortlistFilterField.customFieldValues,
            })) || []),
          )
        }

        return acc
      }, [] as { attribute: string | number; type: ShortlistFilterField; label: string }[]),
    [activeShortlist],
  )

  useEffect(() => {
    if (debouncedFilters === existingFilters) return
    filtersUpdated(
      filters
        .filter(
          filter =>
            !!filter.value || filter.operator === FilterOperator.IS_EMPTY || filter.operator === FilterOperator.IS_NOT_EMPTY,
        )
        .map(filter => ({
          ...filter,
          nestedAttribute: filter.type === ShortlistFilterField.customFieldValues ? (filter.attribute as number) : '',
        })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters])

  const handleAddNewFilter = useCallback(() => {
    trackPendoEvent(
      PendoCustomListFilterEventType.AddOne,
      prepareCustomListFilterEvent({
        eventType: PendoCustomListFilterEventType.AddOne,
        user,
        customListId: activeShortlist?.id,
        filterRowData: DEFAULT_FILTER,
      }),
    )
    setFilters([...filters, DEFAULT_FILTER])
  }, [user, filters, activeShortlist?.id, trackPendoEvent])

  const handleFilterDelete = useCallback(
    (indexToDelete: number) => {
      trackPendoEvent(
        PendoCustomListFilterEventType.RemoveOne,
        prepareCustomListFilterEvent({
          eventType: PendoCustomListFilterEventType.RemoveOne,
          user,
          customListId: activeShortlist?.id,
          filterRowData: filters[indexToDelete],
        }),
      )
      setFilters([...filters.filter((_, index) => index !== indexToDelete)])
    },
    [user, filters, activeShortlist?.id, trackPendoEvent],
  )

  const handleDeleteAll = useCallback(() => {
    trackPendoEvent(
      PendoCustomListFilterEventType.RemoveAll,
      prepareCustomListFilterEvent({
        eventType: PendoCustomListFilterEventType.RemoveAll,
        user,
        customListId: activeShortlist?.id,
      }),
    )
    setFilters([])
  }, [user, activeShortlist?.id, trackPendoEvent])

  const handleFilterUpdate = useCallback(
    (indexToUpdate: number, filter: ShortlistColumnFilter) => {
      const newFilters = [...filters]
      newFilters[indexToUpdate] = { ...filter }

      trackPendoEvent(
        PendoCustomListFilterEventType.UpdateOne,
        prepareCustomListFilterEvent({
          eventType: PendoCustomListFilterEventType.UpdateOne,
          user,
          customListId: activeShortlist?.id,
          filterRowData: filter,
        }),
      )
      setFilters(newFilters)
    },
    [filters, activeShortlist?.id, trackPendoEvent, user],
  )

  const shouldShowValueSelection = (filter: ShortlistColumnFilter) =>
    filter.operator !== FilterOperator.IS_EMPTY && filter.operator !== FilterOperator.IS_NOT_EMPTY

  const valueInputBasedOnOperator = (index: number, filter: ShortlistColumnFilter) => {
    const inputType = SHORTLIST_VALUE_TYPE_BY_OPERATOR[filter.operator]
    switch (inputType) {
      case ShortlistFilterFieldType.TEXT:
        return (
          <>
            <InputLabel
              sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
              id="shortlist-filter-value"
              shrink
            >
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_VALUE)}
            </InputLabel>
            <TextField
              data-testid="shortlist-filter-value"
              value={filter.value}
              onChange={e =>
                handleFilterUpdate(index, {
                  ...filter,
                  value: e.target.value,
                })
              }
            />
          </>
        )
      case ShortlistFilterFieldType.DATE_RANGE:
        // eslint-disable-next-line no-case-declarations
        const valueAsArray = (filter.value?.split(',') as string[]) || []

        return (
          <CustomDateRangePicker
            data-testid="shortlist-filter-value"
            start={valueAsArray[0]}
            end={valueAsArray[1]}
            onDateChange={(start, end) => {
              handleFilterUpdate(index, {
                ...filter,
                value: start && end ? `${start || ''},${end || ''}` : undefined,
              })
            }}
          />
        )
      default:
        return <Box />
    }
  }

  const availableOperatorsByAttribute = useMemo(() => {
    if (!filterColumns) return {} as { [key: string]: FilterOperator[] }

    return filterColumns.reduce((accum, curr) => {
      let operators
      if (curr.type === ShortlistFilterField.customFieldValues) {
        const customField = activeShortlist?.customFields?.find(field => field.id === (curr.attribute as number))
        operators = SHORTLIST_FILTER_OPERATORS_BY_CUSTOM_FIELD[customField?.fieldType as CustomColumnType]
      } else {
        operators = SHORTLIST_FILTER_OPERATORS_BY_FIELD[curr.type]
      }
      return {
        ...accum,
        [curr.attribute.toString()]: operators,
      }
    }, {} as { [key: string]: FilterOperator[] })
  }, [filterColumns, activeShortlist?.customFields])

  const handleFilterFieldChange = useCallback(
    (indexToUpdate: number, attribute: ShortlistFilterField) => {
      const newFilters = [...filters]
      const filterColumn = filterColumns.find(filter => filter.attribute === attribute)

      const newFilter = {
        ...filters[indexToUpdate],
        attribute,
        type: filterColumn!.type,
      }

      if (
        filterColumn?.attribute &&
        !availableOperatorsByAttribute[filterColumn?.attribute.toString()]?.some(operator => operator === newFilter.operator)
      ) {
        const oldInputType = SHORTLIST_VALUE_TYPE_BY_OPERATOR[newFilter.operator]
        const [operatorToSet] = availableOperatorsByAttribute[filterColumn.attribute.toString()]
        newFilter.operator = operatorToSet
        const inputTypeByOperator = SHORTLIST_VALUE_TYPE_BY_OPERATOR[operatorToSet]

        if (oldInputType !== inputTypeByOperator) {
          newFilter.value = undefined
        }
      }

      newFilters[indexToUpdate] = newFilter
      setFilters(newFilters)
    },
    [filters, filterColumns, availableOperatorsByAttribute],
  )

  return (
    <CustomPopover id="filter_shortlist_profiles" anchor={anchor} setAnchor={handleClose}>
      {/* eslint-disable-next-line */}
      <Box padding={isDesktop ? '16px' : '10px'} width={isDesktop ? 990 : isMobile ? '92vw' : '80vw'} position="relative">
        <Box position="absolute" top="8px" right="8px">
          <IconButton onClick={() => handleClose(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box>
          <Box maxWidth="400px">
            <Typography variant="h2" alignContent="center">
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" alignContent="center">
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_POPOVER_TEXT)}
            </Typography>
          </Box>
          {filters.map((filter, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={isMobile ? '5px' : '20px'}
              flexWrap="wrap"
            >
              <Box display="flex" flex="1" alignItems="center">
                <IconButton sx={{ marginTop: '10px', height: 'fit-content' }} onClick={() => handleFilterDelete(index)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box flex="1" visibility={index ? 'inherit' : 'hidden'}>
                  <FormControl style={{ width: '75px', flex: '1 1 auto' }} variant="outlined" margin="normal">
                    <InputLabel
                      sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                      data-testid="shortlist-filter-combination-label"
                      shrink
                    >
                      {t(translationKeys.CUSTOM_LIST_FILTER_LOGIC_COMBINATION)}
                    </InputLabel>
                    <Select
                      data-testid="shortlist-filter-combination"
                      notched
                      value={filter.combinator}
                      labelId="shortlist-filter-combination"
                      onChange={(e: SelectChangeEvent<FilterCombinator | string>) =>
                        handleFilterUpdate(index, {
                          ...filter,
                          combinator: e.target.value as FilterCombinator,
                        })
                      }
                    >
                      <MenuItem value={FilterCombinator.AND}>{t(FilterCombinator.AND)}</MenuItem>
                      <MenuItem value={FilterCombinator.OR}>{t(FilterCombinator.OR)}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <FormControl variant="outlined" sx={{ minWidth: '50px', width: '280px', flex: '1 1 auto' }} margin="normal">
                <InputLabel
                  sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                  data-testid="shortlist-filter-column"
                  shrink
                >
                  {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_COLUMN)}
                </InputLabel>
                <Select
                  date-testid="shortlist-filter-column"
                  notched
                  value={filter.attribute as string}
                  labelId="profile-filter-column"
                  onChange={(e: SelectChangeEvent<string>) =>
                    handleFilterFieldChange(index, e.target.value as ShortlistFilterField)
                  }
                >
                  {filterColumns.map(availableField => (
                    <MenuItem key={availableField.attribute} value={availableField.attribute}>
                      {t(availableField.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" sx={{ minWidth: '50px', width: '180px', flex: '1 1 auto' }} margin="normal">
                <InputLabel
                  sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                  data-testid="shortlist-filter-operator"
                  shrink
                >
                  {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_OPERATOR)}
                </InputLabel>
                <Select
                  id="shortlist-filter-operator"
                  notched
                  value={filter.operator}
                  labelId="shortlist-filter-operator"
                  onChange={(e: SelectChangeEvent<FilterOperator>) =>
                    handleFilterUpdate(index, {
                      ...filter,
                      operator: e.target.value as FilterOperator,
                    })
                  }
                >
                  {availableOperatorsByAttribute[filter.attribute.toString()].map(option => (
                    <MenuItem key={option} value={option}>
                      {t(option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                sx={{
                  minWidth: '50px',
                  width: '320px',
                  visibility: shouldShowValueSelection(filter) ? 'inherit' : 'hidden',
                  flex: '1 1 auto',
                }}
                margin="normal"
              >
                {valueInputBasedOnOperator(index, filter)}
              </FormControl>
            </Box>
          ))}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Button
              variant="text"
              color="primary"
              data-testid="add_new_filter_shortlist"
              startIcon={<AddIcon color="inherit" sx={{ marginLeft: '-10px', fontSize: '32px' }} />}
              onClick={handleAddNewFilter}
              sx={{ marginLeft: '5px' }}
            >
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_ADD_FILTER_BTN)}
            </Button>
            <Button
              variant="text"
              color="primary"
              data-testid="remove_all_filters_shortlist"
              startIcon={<DeleteIcon color="inherit" sx={{ marginLeft: '-10px', fontSize: '32px' }} />}
              onClick={handleDeleteAll}
              sx={{ marginRight: '-5px' }}
            >
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_REMOVE_ALL_BTN)}
            </Button>
          </Box>
        </Box>
      </Box>
    </CustomPopover>
  )
}

export default memo(ShortlistFilterPopover)
